import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DollarSign, RefreshCw } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import PricingBreakdown from '@/components/PricingBreakdown';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Markup, discounts and the profit breakdown for one quote — opened from builders / Components without leaving the page.
export default function PricingDrawer({ api, appraisalId, quoteLabel = '', defaultOpen = false, footer = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const [s, setS] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [markup, setMarkup] = useState('');
  const [extra, setExtra] = useState('');

  const load = useCallback(async () => {
    const [{ data }, cat] = await Promise.all([api.get(`/quote-summary/${appraisalId}`), api.get('/discount-catalogue').catch(() => ({ data: [] }))]);
    setS(data);
    setCatalogue(Array.isArray(cat.data) ? cat.data : []);
    setMarkup(((Number(data.markup || 1) - 1) * 100).toFixed(1));
    setExtra(String(data.extra_discount_amount || 0));
  }, [api, appraisalId]);

  useEffect(() => { if (open) load().catch(() => toast.error('Could not load pricing')); }, [open, load]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/appraisals/${appraisalId}/pricing`, body);
      setS(data);
      toast.success(data.children_updated ? `Saved — also applied to ${data.children_updated} builder quote(s)` : 'Pricing saved');
      window.dispatchEvent(new Event('rtr:components-refresh'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const ref = s?.is_draft ? `Draft ${s?.variant_label || ''}`.trim() : (s?.appraisal_no ? `A${s.appraisal_no}` : '');
  const titleLabel = [quoteLabel || ref, s?.quote_type_ref].filter(Boolean).join(' · ');
  const selected = (s?.discounts || []).map(d => d.key);
  const toggle = (k) => patch({ discounts: selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k] });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" data-testid="pricing-drawer-open"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 transition-colors">
          <DollarSign size={13} /> Pricing
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="pricing-drawer">
        <SheetHeader><SheetTitle data-testid="pricing-drawer-title">Pricing{titleLabel ? ` · ${titleLabel}` : ''} · markup, discounts &amp; profit</SheetTitle></SheetHeader>
        {!s ? <p className="text-sm text-slate-500 mt-6">Loading…</p> : (
          <div className="mt-4 space-y-4 text-sm">
            <div className="flex items-center justify-between rounded-md bg-slate-900 text-white px-3 py-2">
              <span className="text-xs uppercase tracking-[0.18em]">Contract (excl. GST)</span>
              <span data-testid="pricing-drawer-grand" className="font-mono-num font-semibold">{money(s.grand_total)}</span>
            </div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Markup %</span>
              <input data-testid="pricing-drawer-markup" type="number" step="0.5" value={markup} onChange={e => setMarkup(e.target.value)}
                onBlur={() => Math.abs(Number(markup || 0) - (Number(s.markup || 1) - 1) * 100) > 1e-6 && patch({ final_quote_markup: 1 + Number(markup || 0) / 100 })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md font-mono-num" />
            </label>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Discounts</span>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {catalogue.map(d => (
                  <label key={d.key} data-testid={`pricing-drawer-discount-${d.key}`}
                    className={`flex items-center gap-2 px-2 py-1.5 border rounded-md cursor-pointer text-xs transition-colors ${selected.includes(d.key) ? 'bg-[#FFF7ED] border-[#D35400] text-[#A04000]' : 'bg-white border-slate-200 text-slate-700'}`}>
                    <input type="checkbox" checked={selected.includes(d.key)} onChange={() => toggle(d.key)} className="accent-[#D35400] w-3 h-3" />
                    <span className="flex-1 truncate">{d.label}</span><span className="font-mono-num text-[10px]">{d.percent}%</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Extra $ discount</span>
              <input data-testid="pricing-drawer-extra" type="number" step="1" value={extra} onChange={e => setExtra(e.target.value)}
                onBlur={() => Number(extra) !== Number(s.extra_discount_amount || 0) && patch({ extra_discount_amount: Number(extra || 0) })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md font-mono-num" />
            </label>
            <PricingBreakdown summary={s} />
            <button type="button" onClick={() => load()} data-testid="pricing-drawer-refresh"
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"><RefreshCw size={12} /> Refresh after pushing builder changes</button>
          </div>
        )}
        {footer}
      </SheetContent>
    </Sheet>
  );
}
